import React, { type ReactElement, useState, useEffect } from 'react';

import dynamic from 'next/dynamic';

import { redirectToAuthenticatedPath } from '@framework/utils/redirect';
import { getShouldForceAuthenticate } from '@framework/utils/url';

import useAuthenticationStatus from '@hooks/useAuthenticationStatus';

import LoginSuccessPrompt from '@accounts/components/ui/LoginSuccessPrompt/LoginSuccessPrompt';
import useGetSSOSlug from '@accounts/hooks/useGetSSOSlug';
import useLoginError from '@accounts/hooks/useLoginError';
import { AuthenticationType, ProofState } from '@accounts/types/authentication';

const PrimaryLoginWrapper = dynamic(() => import('./wrappers/PrimaryLoginWrapper'));
const SecondaryLoginWrapper = dynamic(() => import('./wrappers/SecondaryLoginWrapper'));
const SSOLogin = dynamic(() => import('./SSOLogin/SSOLogin'));

const Login = (): ReactElement | null => {
    const [authType, setAuthType] = useState<AuthenticationType | undefined>(undefined);

    const [ssoSlug, removeSlugCookie] = useGetSSOSlug();
    const { data: proofStatus } = useAuthenticationStatus();
    const { primary, isAuthenticated } = proofStatus || {};
    const handleLoginError = useLoginError();

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        const shouldForceAuthenticate = getShouldForceAuthenticate();

        if (ssoSlug) {
            setAuthType(AuthenticationType.SSO);
        } else {
            if (isAuthenticated && !shouldForceAuthenticate) {
                return redirectToAuthenticatedPath();
            }
            if (
                primary?.state === ProofState.Proven &&
                primary?.acceptSecondary?.length > 0 &&
                !shouldForceAuthenticate
            ) {
                setAuthType(AuthenticationType.SecondaryFactor);
            } else {
                setAuthType(AuthenticationType.Password);
            }
            removeSlugCookie();
        }
    }, [ssoSlug, isAuthenticated, primary?.state, primary?.acceptSecondary?.length]);

    const handlePrimaryLoginError = (error: unknown) =>
        handleLoginError(error, {
            unAuthorizedCb: () => {
                setAuthType(AuthenticationType.Password);
            }
        });

    const handleLoginWithPassword = () => {
        setAuthType(AuthenticationType.Password);
    };

    const handleLoginWithSecondFactor = () => {
        setAuthType(AuthenticationType.SecondaryFactor);
    };

    const handleLoginWithSSOClick = () => {
        setAuthType(AuthenticationType.SSO);
    };

    const handleShowSuccessPrompt = () => {
        setAuthType(AuthenticationType.Success);
    };

    const stepComponent =
        new Map([
            [
                authType === AuthenticationType.Password,
                <PrimaryLoginWrapper
                    key="primary"
                    onLoginError={handlePrimaryLoginError}
                    onLoginWithSecondFactor={handleLoginWithSecondFactor}
                    onLoginWithSSOClick={handleLoginWithSSOClick}
                />
            ],
            [
                authType === AuthenticationType.SecondaryFactor,
                <SecondaryLoginWrapper
                    key="secondary"
                    onShowSuccessPrompt={handleShowSuccessPrompt}
                    onLoginError={handlePrimaryLoginError}
                />
            ],
            [
                authType === AuthenticationType.SSO,
                <SSOLogin
                    key="sso"
                    onLoginError={handlePrimaryLoginError}
                    onLoginWithPasswordClick={handleLoginWithPassword}
                />
            ],
            [
                authType === AuthenticationType.Success,
                <LoginSuccessPrompt key="loginSuccessPrompt" />
            ]
        ]).get(true) ?? null;

    if (!authType) {
        return null;
    }
    return stepComponent;
};

export default Login;
